import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import { Plan } from 'types/entity'
import numeral from 'numeral'
import { useMemo, useState } from 'react'
import useSubscription from 'core/hooks/user/useSubscription'

/**
 * Just for reference, the Premium V3 Plans product ids.

const premiumV3Plans = [
  'starter-monthly',
  'starter-yearly',
  'starter-monthly-partner',
  'starter-yearly-partner',
  'professional-yearly',
  'professional-yearly-partner',
  'gigpro-starter-monthly',
  'gigpro-starter-yearly'
]

 *
 */

export interface PersonalPlan {
  id: Plan['id']
  color?: string
  badgeText?: string
  save: string | null
  mostPopular: string | null
  isProfessionalPlan: boolean
  billedAsLabel: string
  monthlyPrice: number
  discountedPrice: number
  buttonText: string
  planName: string
  buttonDisabled?: boolean
  interval: Plan['interval']
  trialPeriod?: string
}

export interface TeamPlan {
  id: Plan['id']
  save?: string | null
  isProfessionalPlan: boolean
  planName: string
  color?: string
  price: number
  monthlyPrice: number
  discountedPrice: number
  label: string
  buttonDisabled?: boolean
  buttonText: string
  interval: Plan['interval']
  trialPeriod?: string
}

export const getPriceAfterDiscount = (
  price: number,
  metadata?: Plan['metadata']
) => {
  const pAmount = metadata?.promo_amount

  if (!pAmount) {
    return price
  }

  if (pAmount.endsWith('%')) {
    const promoAmount = parseFloat(pAmount.slice(0, -1))
    if (isNaN(promoAmount)) return price
    const amountToDiscount = (price * promoAmount) / 100
    const discountedPrice = parseFloat((price - amountToDiscount).toFixed(2))
    return discountedPrice > 0 ? discountedPrice : 0
  } else if (pAmount.startsWith('$')) {
    const promoAmount = parseFloat(pAmount.slice(1))
    if (isNaN(promoAmount)) return price
    const discountedPrice = parseFloat((price - promoAmount).toFixed(2))
    return discountedPrice > 0 ? discountedPrice : 0
  }

  return price
}

const legacyPlansThatCanBeTranslatedToPremiumV3 = [
  'stripe.monthly.premium',
  'stripe.yearly.premium',
  'stripe.yearly.premium.trial',
  'play.everlance.premium.year.trial',
  'play.everlance.premium.plus.year.trial',
  'stripe-premium-plus-yearly',
  'stripe-premium-plus-yearly-trial'
]

const usePremiumPlans = () => {
  const {
    permissions: { hasFullPower }
  } = useRolesAndPermissions()
  const { segment } = useSubscription()

  const [groupedPlans, setGroupedPlans] = useState<{
    monthlyPersonalPlans: PersonalPlan[]
    yearlyPersonalPlans: PersonalPlan[]
    monthlyTeamPlans: TeamPlan[]
    yearlyTeamPlans: TeamPlan[]
  }>({
    monthlyPersonalPlans: [],
    yearlyPersonalPlans: [],
    monthlyTeamPlans: [],
    yearlyTeamPlans: []
  })

  const {
    data: plans,
    isFetching,
    refetch
  } = useQueryCallbacks({
    queryKey: ['premium', 'plans'],
    queryFn: APIService.subscriptions.getPlans,
    refetchOnWindowFocus: false,
    onSuccess: (_plans) => {
      const monthlyPersonalPlans: PersonalPlan[] = []
      const yearlyPersonalPlans: PersonalPlan[] = []
      const monthlyTeamPlans: TeamPlan[] = []
      const yearlyTeamPlans: TeamPlan[] = []

      _plans
        ?.filter(
          (plan) =>
            !legacyPlansThatCanBeTranslatedToPremiumV3.includes(
              plan.id.toLowerCase()
            )
        )
        .forEach((plan) => {
          const {
            button_text,
            title,
            Title,
            product,
            Product,
            color,
            badge_text
          } = plan.metadata
          const planName =
            product ||
            Product ||
            title ||
            Title ||
            plan.nickname ||
            `${plan.interval}ly`
          const price = plan.amount / 100
          const discountedPrice = getPriceAfterDiscount(price, plan.metadata)
          const billedAsLabel = `${numeral(discountedPrice).format('$0,0[.]00')} billed ${
            plan.interval
          }ly`
          const monthlyPrice =
            plan.interval === 'year'
              ? parseFloat((discountedPrice / 12).toFixed(2))
              : discountedPrice
          const lowerCasePlanId = plan.id.toLowerCase()
          const isProfessionalPlan = lowerCasePlanId.includes('professional')
          const save: string | null = plan.metadata.promo_amount
            ? `SAVE ${plan.metadata.promo_amount}`
            : null

          const mostPopular: string | null = isProfessionalPlan
            ? 'Most popular'
            : null

          const trialPeriod =
            plan.metadata?.trial_period_unit &&
            plan.metadata?.trial_period_unit_type
              ? `${plan.metadata?.trial_period_unit} ${plan.metadata?.trial_period_unit_type}`
              : plan.trial_period_days
                ? `${plan.trial_period_days} days`
                : ''

          const buttonText =
            button_text ||
            (plan.current_plan
              ? 'Current Plan'
              : trialPeriod
                ? `Start ${trialPeriod} free trial`
                : 'Choose')

          if (hasFullPower) {
            const _teamPlan: TeamPlan = {
              id: plan.id,
              buttonText,
              planName,
              color,
              price,
              monthlyPrice,
              discountedPrice,
              save,
              isProfessionalPlan: isProfessionalPlan,
              label:
                plan.interval === 'month'
                  ? 'per user'
                  : `$${price} per user / ${plan.interval}`,
              interval: plan.interval as Plan['interval'],
              trialPeriod: trialPeriod
            }
            if (plan.interval === 'month') monthlyTeamPlans.push(_teamPlan)
            if (plan.interval === 'year') yearlyTeamPlans.push(_teamPlan)
            return
          }

          const _plan: PersonalPlan = {
            id: plan.id,
            billedAsLabel,
            monthlyPrice,
            discountedPrice,
            buttonText,
            planName,
            color,
            save,
            isProfessionalPlan: isProfessionalPlan,
            mostPopular,
            badgeText: badge_text,
            interval: plan.interval as Plan['interval'],
            trialPeriod: trialPeriod
          }

          if (plan.interval === 'month') monthlyPersonalPlans.push(_plan)
          if (plan.interval === 'year') yearlyPersonalPlans.push(_plan)
        })

      monthlyTeamPlans.sort((a, b) => a.monthlyPrice - b.monthlyPrice)
      yearlyTeamPlans.sort((a, b) => a.monthlyPrice - b.monthlyPrice)
      monthlyPersonalPlans.sort((a, b) => a.monthlyPrice - b.monthlyPrice)
      yearlyPersonalPlans.sort((a, b) => a.monthlyPrice - b.monthlyPrice)

      setGroupedPlans({
        monthlyTeamPlans,
        yearlyTeamPlans,
        yearlyPersonalPlans,
        monthlyPersonalPlans
      })
    }
  })

  const currentPlan = useMemo(() => {
    const _currentPlan = plans?.find((p) => p.current_plan)
    let newCurrentPlan: Plan | undefined

    if (segment === 'premium') {
      newCurrentPlan = plans?.find(
        (p) => p.id.toLowerCase() === `starter-${_currentPlan?.interval}ly`
      )
    }

    if (segment === 'premium_plus' && _currentPlan?.interval === 'year') {
      newCurrentPlan = plans?.find((p) =>
        ['professional-yearly', 'professional-yearly-partner'].includes(
          p.id.toLowerCase()
        )
      )
    }

    if (
      segment === 'premium' ||
      (segment === 'premium_plus' && _currentPlan?.interval === 'year')
    ) {
      return newCurrentPlan
        ? {
            ...newCurrentPlan,
            ...(_currentPlan
              ? {
                  amount: _currentPlan.amount
                }
              : {})
          }
        : null
    }

    return _currentPlan
  }, [plans, segment])

  return {
    ...groupedPlans,
    isLoading: isFetching,
    plans,
    currentPlan,
    refetch
  }
}

export default usePremiumPlans
